import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { BookOpen, Compass, Download, GraduationCap, HelpCircle, PlayCircle, Presentation, Search } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/hooks/useFetch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { PageHeader } from "@/components/States";
import Walkthrough from "@/components/learn/Walkthrough";
import GuidedTour from "@/components/learn/GuidedTour";
import LessonClip from "@/components/learn/LessonClip";
import { AREAS, GLOSSARY, LESSONS, PLAN_NAMES, QA, QA_TOPICS, tierIncluded, tierLabel } from "@/lib/learn";

function TierBadge({ tier, plan }) {
  const inc = tierIncluded(tier, plan);
  return (
    <Badge variant={inc ? "secondary" : "outline"} className={`shrink-0 text-[10px] font-medium ${inc ? "" : "text-muted-foreground"}`} data-testid={`tier-badge-${tier}`}>
      {inc ? "In your plan" : tierLabel(tier)}
    </Badge>
  );
}

function Glossary({ plan }) {
  const [q, setQ] = useState("");
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return GLOSSARY.filter((g) => !s || g.term.toLowerCase().includes(s) || g.def.toLowerCase().includes(s) || (g.aka || "").toLowerCase().includes(s))
      .sort((a, b) => a.term.localeCompare(b.term));
  }, [q]);
  return (
    <div>
      <div className="relative mb-5 max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search terms…" className="pl-9" data-testid="glossary-search" />
      </div>
      <div className="grid gap-3 sm:grid-cols-2" data-testid="glossary-list">
        {list.map((g) => (
          <div key={g.term} className="rounded-xl border bg-card p-4" data-testid={`glossary-term-${g.term.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <div className="flex items-center justify-between gap-2">
              <h3 className="font-semibold">{g.term}{g.aka && <span className="ml-2 text-xs font-normal text-muted-foreground">also “{g.aka}”</span>}</h3>
              <TierBadge tier={g.tier} plan={plan} />
            </div>
            <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{g.def}</p>
          </div>
        ))}
        {!list.length && <p className="text-sm text-muted-foreground">No terms match “{q}”.</p>}
      </div>
    </div>
  );
}

function StudyGuide({ plan, clips, isAdmin, reloadClips }) {
  const areas = AREAS.filter(([k]) => LESSONS.some((l) => l.area === k));
  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-2xl text-sm text-muted-foreground">Read-in-app lessons grouped by area. Each explains what the feature is, who uses it and why it matters.</p>
        <Button asChild variant="outline" size="sm" className="rounded-full"><a href="/learn/guide/print" target="_blank" rel="noreferrer" data-testid="guide-print-link"><Download className="mr-1.5 h-3.5 w-3.5" />Download guide</a></Button>
      </div>
      {areas.map(([key, name]) => (
        <section key={key} className="mb-7" data-testid={`guide-area-${key}`}>
          <h2 className="section-title mb-2">{name}</h2>
          <Accordion type="single" collapsible className="rounded-xl border bg-card px-4">
            {LESSONS.filter((l) => l.area === key).map((l) => (
              <AccordionItem key={l.id} value={l.id} data-testid={`lesson-${l.id}`}>
                <AccordionTrigger className="text-left hover:no-underline">
                  <span className="flex flex-1 items-center justify-between gap-3 pr-3">
                    <span className="font-medium">{l.title}</span>
                    <TierBadge tier={l.tier} plan={plan} />
                  </span>
                </AccordionTrigger>
                <AccordionContent>
                  <p className="text-[11px] uppercase tracking-wider text-muted-foreground">For {l.who}</p>
                  <p className="mt-2 text-sm leading-relaxed">{l.what}</p>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground"><span className="font-medium text-foreground">Why it matters: </span>{l.why}</p>
                  <ul className="mt-3 space-y-1.5">
                    {l.points.map((p) => <li key={p} className="flex gap-2 text-sm"><span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-accent" />{p}</li>)}
                  </ul>
                  <LessonClip lessonId={l.id} clip={clips?.[l.id]} isAdmin={isAdmin} onChange={reloadClips} />
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      ))}
    </div>
  );
}

function QaBank({ plan }) {
  const [q, setQ] = useState("");
  const [topic, setTopic] = useState("All");
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return QA.filter((x) => (topic === "All" || x.topic === topic) && (!s || x.q.toLowerCase().includes(s) || x.a.toLowerCase().includes(s)));
  }, [q, topic]);
  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="relative max-w-md flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search questions…" className="pl-9" data-testid="qa-search" />
        </div>
        <Button asChild variant="outline" size="sm" className="rounded-full"><a href="/learn/qa/print" target="_blank" rel="noreferrer" data-testid="qa-print-link"><Download className="mr-1.5 h-3.5 w-3.5" />Download Q&A</a></Button>
      </div>
      <div className="mb-5 flex flex-wrap gap-1.5" data-testid="qa-topics">
        {["All", ...QA_TOPICS].map((t) => (
          <button key={t} onClick={() => setTopic(t)} data-testid={`qa-topic-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className={`rounded-full px-3 py-1 text-xs transition-colors ${topic === t ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>{t}</button>
        ))}
      </div>
      <Accordion type="single" collapsible className="rounded-xl border bg-card px-4" data-testid="qa-list">
        {list.map((x) => {
          const slug = x.q.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 48);
          return (
            <AccordionItem key={slug} value={slug} data-testid={`qa-item-${slug}`}>
              <AccordionTrigger className="text-left hover:no-underline">
                <span className="flex flex-1 items-center justify-between gap-3 pr-3"><span className="font-medium">{x.q}</span><TierBadge tier={x.tier} plan={plan} /></span>
              </AccordionTrigger>
              <AccordionContent><p className="text-sm leading-relaxed text-muted-foreground">{x.a}</p></AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
      {!list.length && <p className="mt-4 text-sm text-muted-foreground">No questions match your search.</p>}
    </div>
  );
}

function Start({ plan, onWalk, onTour, owner }) {
  const planName = PLAN_NAMES[plan] || "your plan";
  return (
    <div className="space-y-6">
      <div className="overflow-hidden rounded-2xl border bg-gradient-to-br from-stone-900 to-stone-800 p-8 text-white sm:p-10" data-testid="learn-hero">
        <p className="eyebrow !text-white/50">Welcome to the Learning Centre</p>
        <h2 className="mt-3 max-w-xl font-serif text-3xl leading-tight sm:text-4xl">Learn E.L.M in a couple of minutes.</h2>
        <p className="mt-4 max-w-lg text-sm text-white/70 sm:text-base">A narrated walkthrough, a guided tour of the real app, plus a glossary, study guide and a full Q&A — all tagged with what {planName} includes.</p>
        <div className="mt-6 flex flex-wrap gap-2">
          <Button className="rounded-full bg-white text-stone-900 hover:bg-white/90" onClick={onWalk} data-testid="learn-start-walkthrough"><PlayCircle className="mr-1.5 h-4 w-4" />Watch the walkthrough</Button>
          <Button variant="outline" className="rounded-full border-white/30 bg-transparent text-white hover:bg-white/10" onClick={onTour} data-testid="learn-start-tour"><Compass className="mr-1.5 h-4 w-4" />Take the guided tour</Button>
        </div>
      </div>
      {owner && (
        <div className="rounded-2xl border bg-card p-6" data-testid="pitch-kit">
          <div className="flex items-center gap-2"><Presentation className="h-4 w-4 text-accent" /><h3 className="section-title">Owner's Pitch Kit</h3></div>
          <p className="mt-1.5 text-sm text-muted-foreground">Ready-to-present material for showing E.L.M to other builders. Open and present, or save each as a PDF.</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button asChild variant="outline" size="sm" className="rounded-full"><Link to="/platform/pitch" data-testid="kit-deck-link"><Presentation className="mr-1.5 h-3.5 w-3.5" />Pitch deck</Link></Button>
            <Button asChild variant="outline" size="sm" className="rounded-full"><a href="/learn/guide/print" target="_blank" rel="noreferrer" data-testid="kit-guide-link"><BookOpen className="mr-1.5 h-3.5 w-3.5" />Study guide</a></Button>
            <Button asChild variant="outline" size="sm" className="rounded-full"><a href="/learn/qa/print" target="_blank" rel="noreferrer" data-testid="kit-qa-link"><HelpCircle className="mr-1.5 h-3.5 w-3.5" />Q&A handout</a></Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Learn() {
  const { user, workspace } = useAuth();
  const plan = workspace?.state?.plan;
  const owner = !!user?.platform_owner;
  const [walk, setWalk] = useState(false);
  const [tour, setTour] = useState(false);
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "start";
  const clips = useFetch("/learn/clips");
  const isAdmin = user?.role === "admin";

  return (
    <div className="mx-auto max-w-5xl px-5 py-8 sm:px-8" data-testid="learn-page">
      {walk && <Walkthrough onClose={() => setWalk(false)} />}
      {tour && <GuidedTour onClose={() => setTour(false)} />}
      <PageHeader eyebrow={<span className="inline-flex items-center gap-1.5"><GraduationCap className="h-3.5 w-3.5" />Learn</span>} title="Learning Centre" />
      <Tabs value={tab} onValueChange={(v) => setSp({ tab: v })}>
        <TabsList className="mb-6 flex-wrap" data-testid="learn-tabs">
          <TabsTrigger value="start" data-testid="learn-tab-start">Start here</TabsTrigger>
          <TabsTrigger value="glossary" data-testid="learn-tab-glossary">Glossary</TabsTrigger>
          <TabsTrigger value="guide" data-testid="learn-tab-guide">Study guide</TabsTrigger>
          <TabsTrigger value="qa" data-testid="learn-tab-qa">Q&amp;A</TabsTrigger>
        </TabsList>
        <TabsContent value="start"><Start plan={plan} owner={owner} onWalk={() => setWalk(true)} onTour={() => setTour(true)} /></TabsContent>
        <TabsContent value="glossary"><Glossary plan={plan} /></TabsContent>
        <TabsContent value="guide"><StudyGuide plan={plan} clips={clips.data || {}} isAdmin={isAdmin} reloadClips={clips.reload} /></TabsContent>
        <TabsContent value="qa"><QaBank plan={plan} /></TabsContent>
      </Tabs>
    </div>
  );
}
